// frontend/src/pages/WorkflowPage.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-04-15
// Updated:      2026-10-15 12:48 BST
//
// Change Log (GMT/BST):
//   2026-10-15 12:48 BST - Fixed cross-version stale step selection, duplicate action loads,
//                           and stale action responses during version/step transitions.
//   2026-10-13 15:05 BST - Added Checklist Builder panel; WorkflowPage remains owner of checklist API mutations.
//   2026-10-06 17:58 BST - Removed frontend ATC seed action, added template Archive handling,
//                           and retained backend-managed workflow lifecycle behaviour.
//   2026-10-06 17:42 BST - Added guarded ATC Work Package workflow seed action using the existing
//                           template-step API. Seed creates business tasks/groups and stores
//                           location/evidence/recurrence authoring metadata in config_json without
//                           pretending those metadata contracts are runtime-enforced yet.
//   2026-10-06 16:30 BST - Phase 1C.3 UX correction: publish readiness now understands
//                           backend-managed lifecycle milestones; Action Configuration is
//                           presented as Advanced Automation.
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:
//   Workflow Builder Page (Phase 4 entry point)
//
//   Responsibilities:
//     - Provide resizable and draggable grid layout for workflow administration
//     - Host template list, draft/version context, steps, actions, bindings
//     - Persist workflow builder panel selection + layout per tenant/user
//     - Load and manage workflow template + version state
//     - Load and manage workflow step state
//     - Load and manage workflow step action state
//     - Load and manage workflow binding state
//     - Load and manage governed workflow template categories
//
//   Important:
//     - Uses standard grid page layout (Dashboard/UserManagement pattern)
//     - Uses WidgetWrapper for consistent app-wide widget rendering
//     - Panels are draggable via WidgetWrapper header drag handle
//     - Panel bodies are scrollable to prevent clipping
//     - Existing workflow logic is preserved
//
//   Q20 (2026-09-02):
//     - Super Admin, System Admin and System Manager can add/remove workflow panels.
//     - Removed panels stay removed across refreshes.
//     - Default panels are first-use defaults only.
//     - Panel selection and layout are scoped by authenticated tenant + user.
//     - Uses canonical AuthContext identity; no JWT decoding.
//     - WidgetWrapper remains the shared global wrapper and is not modified.
//
//   Mobile Responsiveness (2026-09-07):
//     - Added a dedicated <=768px workflow presentation.
//     - Mobile renders active workflow panels as a natural single-column stream.
//     - Mobile ordering follows persisted desktop layout y/x positions.
//     - Mobile viewing never writes mobile geometry back to desktop layout storage.
//     - Desktop react-grid-layout drag/resize behaviour remains unchanged.
//     - Existing workflow state, API calls, widget permissions and Q20 persistence remain unchanged.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import { useAuth } from "../context/AuthContext";
import WidgetWrapper from "../components/WidgetWrapper.jsx";
import AddWidgetModal from "../components/AddWidgetModal";

import WorkflowTemplateListWidget from "../widgets/WorkflowTemplateListWidget.jsx";
import WorkflowDraftHeaderWidget from "../widgets/WorkflowDraftHeaderWidget.jsx";
import WorkflowStepBuilderWidget from "../widgets/WorkflowStepBuilderWidget.jsx";
import WorkflowChecklistBuilderWidget from "../widgets/WorkflowChecklistBuilderWidget.jsx";
import WorkflowActionConfigWidget from "../widgets/WorkflowActionConfigWidget.jsx";
import WorkflowBindingAdminWidget from "../widgets/WorkflowBindingAdminWidget.jsx";
import WorkflowValidationSummaryWidget from "../widgets/WorkflowValidationSummaryWidget.jsx";
import WorkflowPublishControlsWidget from "../widgets/WorkflowPublishControlsWidget.jsx";

import {
  archiveWorkflowTemplate,
  createWorkflowTemplate,
  createWorkflowTemplateBinding,
  createWorkflowTemplateDraft,
  createWorkflowTemplateStep,
  createWorkflowTemplateStepAction,
  deactivateWorkflowTemplateBinding,
  deleteWorkflowTemplateStep,
  deleteWorkflowTemplateStepAction,
  getTaxonomyTree,
  getWorkPackageTaxonomyTree,
  getWorkflowTemplateVersion,
  listWorkflowTemplateBindings,
  listWorkflowTemplateCategories,
  listWorkflowTemplateStepActions,
  listWorkflowTemplateSteps,
  listWorkflowTemplateVersions,
  listWorkflowTemplates,
  publishWorkflowTemplateVersion,
  updateWorkflowTemplate,
  updateWorkflowTemplateBinding,
  updateWorkflowTemplateStep,
  updateWorkflowTemplateStepAction,
} from "../api/workflowAdmin";
import { listWorkflowTemplateChecklists, createWorkflowTemplateChecklist, updateWorkflowTemplateChecklist, setWorkflowTemplateChecklistSteps, deleteWorkflowTemplateChecklist } from "../api/workflowChecklistAdmin";

const MANAGE_WIDGET_ROLES = new Set(["Super Admin", "System Admin", "System Manager"]);
const MANAGE_WIDGET_ROLE_IDS = new Set([1, 3, 4]);
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", apply);
    else mediaQuery.addListener(apply);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", apply);
      else mediaQuery.removeListener(apply);
    };
  }, [breakpoint]);

  return isMobile;
}

const getRoleName = (user) => {
  const direct = user?.role_name ?? user?.roleName;
  if (direct) return String(direct);
  if (typeof user?.role === "string") return user.role;
  return String(user?.role?.name ?? user?.role?.role_name ?? user?.role?.title ?? user?.role?.label ?? "");
};

const canManageWidgetsForUser = (user) => {
  const roleName = getRoleName(user);
  const roleId = Number(user?.role_id ?? user?.role?.id ?? 0);
  return MANAGE_WIDGET_ROLES.has(roleName) || MANAGE_WIDGET_ROLE_IDS.has(roleId);
};

const buildStorageKeys = (user) => {
  const tenantId = user?.tenant_id ?? "anonTenant";
  const userId = user?.id ?? user?.user_id ?? user?.username ?? "anonUser";
  return {
    layout: `workflowPageLayout_${tenantId}_${userId}`,
    widgets: `workflowPageWidgets_${tenantId}_${userId}`,
  };
};

const safeJsonParse = (raw) => {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const sameId = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a) === String(b);
};

const defaultLayout = [
  { i: "template_list", x: 0, y: 0, w: 4, h: 12, minW: 3, minH: 8, maxW: 6 },
  { i: "draft_header", x: 4, y: 0, w: 8, h: 8, minW: 5, minH: 6, maxW: 12 },
  { i: "validation", x: 4, y: 8, w: 8, h: 4, minW: 5, minH: 4, maxW: 12 },

  { i: "steps", x: 0, y: 12, w: 7, h: 10, minW: 5, minH: 8, maxW: 9 },
  { i: "actions", x: 7, y: 12, w: 5, h: 10, minW: 3, minH: 8, maxW: 7 },
  { i: "checklists", x: 0, y: 22, w: 12, h: 10, minW: 6, minH: 8, maxW: 12 },

  { i: "bindings", x: 0, y: 32, w: 8, h: 7, minW: 5, minH: 5, maxW: 10 },
  { i: "publish", x: 8, y: 32, w: 4, h: 7, minW: 2, minH: 5, maxW: 6 },
];

const DEFAULT_WIDGET_IDS = defaultLayout.map((item) => item.i);
const DEFAULT_WIDGET_SET = new Set(DEFAULT_WIDGET_IDS);

const WORKFLOW_WIDGET_CATALOG = [
  { id: "template_list", widget_id: "template_list", friendly_name: "Templates" },
  { id: "draft_header", widget_id: "draft_header", friendly_name: "Draft / Version Context" },
  { id: "validation", widget_id: "validation", friendly_name: "Validation Summary" },
  { id: "steps", widget_id: "steps", friendly_name: "Step Builder" },
  { id: "actions", widget_id: "actions", friendly_name: "Advanced Automation" },
  { id: "checklists", widget_id: "checklists", friendly_name: "Checklists" },
  { id: "bindings", widget_id: "bindings", friendly_name: "Bindings" },
  { id: "publish", widget_id: "publish", friendly_name: "Publish Controls" },
];

const validateLayout = (candidateLayout, activeIds) => {
  const allowed = new Set((activeIds || []).map(String));
  const source = Array.isArray(candidateLayout) ? candidateLayout : [];

  const normalized = source
    .filter((item) => item && typeof item.i === "string" && allowed.has(item.i))
    .map((item) => {
      const base = defaultLayout.find((entry) => entry.i === item.i) || {};
      return {
        ...base,
        ...item,
        i: String(item.i),
        x: Number.isFinite(Number(item.x)) ? Number(item.x) : base.x ?? 0,
        y: Number.isFinite(Number(item.y)) ? Number(item.y) : base.y ?? 0,
        w: Number.isFinite(Number(item.w)) ? Number(item.w) : base.w ?? 3,
        h: Number.isFinite(Number(item.h)) ? Number(item.h) : base.h ?? 3,
      };
    });

  const byId = new Map(normalized.map((item) => [item.i, item]));
  const complete = [];

  for (const id of allowed) {
    complete.push(
      byId.get(id) ||
        {
          ...(defaultLayout.find((entry) => entry.i === id) || {
            i: id,
            x: 0,
            y: 0,
            w: 3,
            h: 3,
          }),
        }
    );
  }

  return normaliseLayout(complete);
};

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(Number(item?.y)) ? Number(item.y) : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const WorkflowPage = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const canManageWidgets = canManageWidgetsForUser(user);
  const storageKeys = useMemo(
    () => buildStorageKeys(user),
    [user?.tenant_id, user?.id, user?.user_id, user?.username]
  );

  const gridRef = useRef(null);
  const [gridWidth, setGridWidth] = useState(1200);
  const [layout, setLayout] = useState([]);
  const [activeWidgetIds, setActiveWidgetIds] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [versions, setVersions] = useState([]);
  const [steps, setSteps] = useState([]);
  const [actions, setActions] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [taxonomyTypes, setTaxonomyTypes] = useState([]);
  const [workPackageTaxonomyTypes, setWorkPackageTaxonomyTypes] = useState([]);
  const [workflowTemplateCategories, setWorkflowTemplateCategories] = useState([]);

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [selectedBindingId, setSelectedBindingId] = useState(null);
  const actionRequestIdRef = useRef(0);

  const [filters, setFilters] = useState({
    include_archived: false,
    status: "",
    category: "",
    search: "",
  });

  const [loading, setLoading] = useState({
    templates: false,
    versions: false,
    steps: false,
    actions: false,
    bindings: false,
    taxonomy: false,
    workPackageTaxonomy: false,
    categories: false,
    creatingTemplate: false,
    creatingDraft: false,
    archivingTemplate: false,
    savingTemplate: false,
    publishing: false,
    savingStep: false,
    deletingStep: false,
    reorderingStep: false,
    checklists: false,
    savingChecklist: false,
    savingAction: false,
    deletingAction: false,
    reorderingAction: false,
    savingBinding: false,
    deactivatingBinding: false,
  });

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;

  const selectedTemplate = useMemo(
    () => templates.find((t) => sameId(t.id, selectedTemplateId)) || null,
    [templates, selectedTemplateId]
  );

  const selectedVersion = useMemo(
    () => versions.find((v) => sameId(v.id, selectedVersionId)) || null,
    [versions, selectedVersionId]
  );

  const selectedStep = useMemo(
    () => steps.find((s) => sameId(s.id, selectedStepId)) || null,
    [steps, selectedStepId]
  );

  const validationSummary = useMemo(() => {
    const errors = [];
    const warnings = [];
    const info = [];

    if (!selectedTemplate) {
      errors.push("No workflow template selected.");
    } else {
      if (!String(selectedTemplate.template_key || "").trim()) {
        errors.push("Template key is missing.");
      }
      if (!String(selectedTemplate.name || "").trim()) {
        errors.push("Template name is missing.");
      }
    }

    if (!selectedVersion) {
      errors.push("No workflow version selected.");
    } else if (!["draft", "published", "retired"].includes(String(selectedVersion.status || "").trim())) {
      warnings.push("Version status is unexpected.");
    }

    if (!Array.isArray(steps) || steps.length === 0) {
      errors.push("Workflow has no steps.");
    } else {
      const sequenceSet = new Set();
      const stepKeySet = new Set();
      const opened = steps.filter((s) => String(s.step_key || "").trim().toLowerCase() === "workflow_opened");
      const completed = steps.filter((s) => String(s.step_key || "").trim().toLowerCase() === "workflow_completed");

      if (opened.length !== 1 || completed.length !== 1) {
        info.push("System lifecycle markers will be prepared automatically when this draft is published.");
      }
      [...opened, ...completed].forEach((step) => {
        if (!step.is_system_locked || step.step_kind !== "system_milestone" || step.completion_mode !== "system_event" || step.assignment_mode !== "unassigned") {
          errors.push("A protected workflow lifecycle marker has an invalid system configuration.");
        }
      });

      const businessSteps = steps.filter((step) => !["workflow_opened", "workflow_completed"].includes(String(step.step_key || "").trim().toLowerCase()));
      if (businessSteps.length === 0) errors.push("Add at least one task before publishing.");


      steps.forEach((step, index) => {
        const seq = Number(step.sequence_no);
        const stepKey = String(step.step_key || "").trim();

        if (!Number.isFinite(seq) || seq <= 0) {
          errors.push(`Step "${step.title || step.id}" has an invalid sequence number.`);
        }

        if (sequenceSet.has(seq)) {
          errors.push(`Duplicate step sequence detected at ${seq}.`);
        } else {
          sequenceSet.add(seq);
        }

        if (!stepKey) {
          errors.push(`Step at sequence ${seq || index + 1} is missing a step key.`);
        } else if (stepKeySet.has(stepKey)) {
          errors.push(`Duplicate step key detected: ${stepKey}.`);
        } else {
          stepKeySet.add(stepKey);
        }

        if (!String(step.title || "").trim()) {
          errors.push(`Step "${stepKey || step.id}" is missing a title.`);
        }

        if (!String(step.step_kind || "").trim()) {
          errors.push(`Step "${step.title || stepKey || step.id}" is missing a step kind.`);
        }

        if (!String(step.completion_mode || "").trim()) {
          errors.push(`Step "${step.title || stepKey || step.id}" is missing a completion mode.`);
        }

        if (!String(step.assignment_mode || "").trim()) {
          errors.push(`Step "${step.title || stepKey || step.id}" is missing an assignment mode.`);
        }

        if (step.allow_skip && step.is_required) {
          warnings.push(`Step "${step.title || stepKey || step.id}" is marked required and skippable.`);
        }

        if (step.step_kind === "approval" && step.completion_mode !== "approval") {
          warnings.push(
            `Approval step "${step.title || stepKey || step.id}" should normally use approval completion mode.`
          );
        }
      });

      const ordered = [...steps].sort((a, b) => Number(a.sequence_no) - Number(b.sequence_no));
      ordered.forEach((step, idx) => {
        const expected = idx + 1;
        if (Number(step.sequence_no) !== expected) {
          warnings.push("Step sequencing is not contiguous from 1.");
        }
      });
    }

    if (Array.isArray(actions) && actions.length > 0) {
      const actionKeys = new Set();
      const actionSeqs = new Set();

      actions.forEach((action) => {
        const actionKey = String(action.action_key || "").trim();
        const seq = Number(action.sequence_no);

        if (!actionKey) {
          errors.push(`Action ${action.id} is missing an action key.`);
        } else if (actionKeys.has(actionKey)) {
          warnings.push(`Duplicate action key detected in selected step: ${actionKey}.`);
        } else {
          actionKeys.add(actionKey);
        }

        if (!Number.isFinite(seq) || seq <= 0) {
          errors.push(`Action "${action.display_name || action.id}" has an invalid sequence number.`);
        } else if (actionSeqs.has(seq)) {
          warnings.push(`Duplicate action sequence detected in selected step: ${seq}.`);
        } else {
          actionSeqs.add(seq);
        }

        if (!String(action.display_name || "").trim()) {
          errors.push(`Action "${actionKey || action.id}" is missing a display name.`);
        }

        if (!String(action.action_type || "").trim()) {
          errors.push(`Action "${action.display_name || actionKey || action.id}" is missing an action type.`);
        }

        if (!String(action.on_event || "").trim()) {
          errors.push(`Action "${action.display_name || actionKey || action.id}" is missing an event trigger.`);
        }

        if (!String(action.failure_mode || "").trim()) {
          errors.push(`Action "${action.display_name || actionKey || action.id}" is missing a failure mode.`);
        }
      });
    } else if (Array.isArray(steps) && steps.length > 0) {
      info.push("No actions configured for the currently selected step.");
    }

    if (!Array.isArray(bindings) || bindings.length === 0) {
      warnings.push("No bindings exist. This workflow will not auto-attach to taxonomy-driven jobs yet.");
    } else {
      const activeBindings = bindings.filter((b) => !!b.is_active);
      if (activeBindings.length === 0) {
        warnings.push("All bindings are inactive.");
      }

      const bindingPairs = new Set();
      activeBindings.forEach((binding) => {
        const targetKey =
          binding.binding_scope === "job_type"
            ? binding.taxonomy_type_id
            : binding.binding_scope === "job_subtype"
              ? binding.taxonomy_subtype_id
              : binding.binding_scope === "job_subtype2"
                ? binding.taxonomy_subtype2_id
                : binding.binding_scope === "work_package_type"
                  ? binding.work_package_type_id
                  : binding.binding_scope === "work_package_subtype"
                    ? binding.work_package_subtype_id
                    : binding.work_package_subtype2_id;

        const pairKey = `${binding.binding_scope}:${targetKey}:${binding.priority}`;
        if (bindingPairs.has(pairKey)) {
          warnings.push(`Duplicate active binding precedence detected for scope ${binding.binding_scope}.`);
        } else {
          bindingPairs.add(pairKey);
        }
      });

      const defaultCount = activeBindings.filter((b) => !!b.is_default).length;
      if (defaultCount > 1) {
        warnings.push("Multiple active default bindings exist.");
      }
    }

    return {
      errors,
      warnings,
      info,
      isReadyToPublish: errors.length === 0,
    };
  }, [selectedTemplate, selectedVersion, steps, actions, bindings]);

  const loadTemplates = useCallback(async () => {
    setLoading((p) => ({ ...p, templates: true }));
    try {
      const params = {
        include_archived: filters.include_archived,
        status: filters.status || undefined,
      };

      if (filters.category) {
        params.category_id = filters.category;
      }

      const data = await listWorkflowTemplates(params);
      const items = Array.isArray(data?.items) ? data.items : [];
      setTemplates(items);
    } catch (err) {
      console.error("Failed to load workflow templates:", err?.response?.data || err?.message || err);
      alert("Failed to load workflow templates.");
    } finally {
      setLoading((p) => ({ ...p, templates: false }));
    }
  }, [filters.include_archived, filters.status, filters.category]);

  const loadWorkflowTemplateCategories = useCallback(async () => {
    setLoading((p) => ({ ...p, categories: true }));
    try {
      const data = await listWorkflowTemplateCategories({ include_inactive: false });
      const items = Array.isArray(data?.items) ? data.items : [];
      setWorkflowTemplateCategories(items);
    } catch (err) {
      console.error(
        "Failed to load workflow template categories:",
        err?.response?.data || err?.message || err
      );
      alert("Failed to load workflow template categories.");
      setWorkflowTemplateCategories([]);
    } finally {
      setLoading((p) => ({ ...p, categories: false }));
    }
  }, []);

  const loadTaxonomyTypes = useCallback(async () => {
    setLoading((p) => ({ ...p, taxonomy: true }));
    try {
      const data = await getTaxonomyTree({ active_only: true });
      const items = Array.isArray(data?.items) ? data.items : [];
      setTaxonomyTypes(items);
    } catch (err) {
      console.error("Failed to load taxonomy tree:", err?.response?.data || err?.message || err);
      alert("Failed to load taxonomy types.");
      setTaxonomyTypes([]);
    } finally {
      setLoading((p) => ({ ...p, taxonomy: false }));
    }
  }, []);

  const loadWorkPackageTaxonomyTypes = useCallback(async () => {
    setLoading((p) => ({ ...p, workPackageTaxonomy: true }));
    try {
      const data = await getWorkPackageTaxonomyTree({ active_only: true });
      const items = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
      setWorkPackageTaxonomyTypes(items);
    } catch (err) {
      console.error(
        "Failed to load Work Package taxonomy tree:",
        err?.response?.data || err?.message || err
      );
      alert("Failed to load Work Package taxonomy types.");
      setWorkPackageTaxonomyTypes([]);
    } finally {
      setLoading((p) => ({ ...p, workPackageTaxonomy: false }));
    }
  }, []);

  const loadBindings = useCallback(async (workflowTemplateId) => {
    if (!workflowTemplateId) {
      setBindings([]);
      setSelectedBindingId(null);
      return;
    }

    setLoading((p) => ({ ...p, bindings: true }));
    try {
      const data = await listWorkflowTemplateBindings({
        workflow_template_id: workflowTemplateId,
      });
      const items = Array.isArray(data?.items) ? data.items : [];
      const sorted = [...items].sort((a, b) => {
        if (a.binding_scope !== b.binding_scope) {
          return String(a.binding_scope).localeCompare(String(b.binding_scope));
        }

        const targetIdForBinding = (binding) => {
          if (binding.binding_scope === "job_type") return Number(binding.taxonomy_type_id || 0);
          if (binding.binding_scope === "job_subtype") return Number(binding.taxonomy_subtype_id || 0);
          if (binding.binding_scope === "job_subtype2") return Number(binding.taxonomy_subtype2_id || 0);
          if (binding.binding_scope === "work_package_type") return Number(binding.work_package_type_id || 0);
          if (binding.binding_scope === "work_package_subtype") return Number(binding.work_package_subtype_id || 0);
          return Number(binding.work_package_subtype2_id || 0);
        };

        const aTarget = targetIdForBinding(a);
        const bTarget = targetIdForBinding(b);

        if (aTarget !== bTarget) return aTarget - bTarget;
        return Number(a.priority) - Number(b.priority);
      });

      setBindings(sorted);

      setSelectedBindingId((prev) => {
        if (prev && sorted.some((b) => sameId(b.id, prev))) return prev;
        return sorted[0]?.id || null;
      });
    } catch (err) {
      console.error("Failed to load workflow template bindings:", err?.response?.data || err?.message || err);
      alert("Failed to load workflow template bindings.");
      setBindings([]);
      setSelectedBindingId(null);
    } finally {
      setLoading((p) => ({ ...p, bindings: false }));
    }
  }, []);

  const loadActions = useCallback(async (workflowTemplateId, workflowTemplateVersionId, workflowTemplateStepId) => {
    if (!workflowTemplateId || !workflowTemplateVersionId || !workflowTemplateStepId) {
      setActions([]);
      setSelectedActionId(null);
      return;
    }

    const requestId = ++actionRequestIdRef.current;
    setLoading((p) => ({ ...p, actions: true }));
    try {
      const data = await listWorkflowTemplateStepActions(
        workflowTemplateId,
        workflowTemplateVersionId,
        workflowTemplateStepId
      );
      if (requestId !== actionRequestIdRef.current) return;
      const items = Array.isArray(data?.items) ? data.items : [];
      const sorted = [...items].sort((a, b) => a.sequence_no - b.sequence_no);
      setActions(sorted);

      setSelectedActionId((prev) => {
        if (prev && sorted.some((a) => sameId(a.id, prev))) return prev;
        return sorted[0]?.id || null;
      });
    } catch (err) {
      if (requestId !== actionRequestIdRef.current) return;
      console.error("Failed to load workflow template step actions:", err?.response?.data || err?.message || err);
      alert("Failed to load workflow template step actions.");
      setActions([]);
      setSelectedActionId(null);
    } finally {
      if (requestId === actionRequestIdRef.current) {
        setLoading((p) => ({ ...p, actions: false }));
      }
    }
  }, []);

  const loadChecklists = useCallback(async (workflowTemplateId, workflowTemplateVersionId) => {
    if (!workflowTemplateId || !workflowTemplateVersionId) { setChecklists([]); return; }
    setLoading((p) => ({ ...p, checklists: true }));
    try { const data = await listWorkflowTemplateChecklists(workflowTemplateId, workflowTemplateVersionId); setChecklists(Array.isArray(data?.items) ? data.items : []); }
    catch (err) { console.error("Failed to load workflow checklists:", err?.response?.data || err?.message || err); setChecklists([]); }
    finally { setLoading((p) => ({ ...p, checklists: false })); }
  }, []);

  const loadSteps = useCallback(async (workflowTemplateId, workflowTemplateVersionId) => {
    if (!workflowTemplateId || !workflowTemplateVersionId) {
      setSteps([]);
      setSelectedStepId(null);
      setActions([]);
      setSelectedActionId(null);
      return;
    }

    setLoading((p) => ({ ...p, steps: true }));
    try {
      const data = await listWorkflowTemplateSteps(workflowTemplateId, workflowTemplateVersionId);
      const items = Array.isArray(data?.items) ? data.items : [];
      const sorted = [...items].sort((a, b) => a.sequence_no - b.sequence_no);
      setSteps(sorted);

      setSelectedStepId((prev) => {
        if (prev && sorted.some((step) => sameId(step.id, prev))) return prev;
        return sorted[0]?.id || null;
      });

      await loadChecklists(workflowTemplateId, workflowTemplateVersionId);
      // Action loading is owned by the selected-step effect, after step membership
      // in the currently selected version has been established.
    } catch (err) {
      console.error("Failed to load workflow template steps:", err?.response?.data || err?.message || err);
      alert("Failed to load workflow template steps.");
      setSteps([]);
      setSelectedStepId(null);
      setActions([]);
      setSelectedActionId(null);
    } finally {
      setLoading((p) => ({ ...p, steps: false }));
    }
  }, [loadChecklists]);

  const loadVersions = useCallback(
    async (workflowTemplateId, nextSelectedVersionId = null) => {
      if (!workflowTemplateId) {
        setVersions([]);
        setSelectedVersionId(null);
        setSteps([]);
        setSelectedStepId(null);
        setActions([]);
        setSelectedActionId(null);
        return;
      }

      setLoading((p) => ({ ...p, versions: true }));
      try {
        const data = await listWorkflowTemplateVersions(workflowTemplateId);
        const items = Array.isArray(data?.items) ? data.items : [];
        setVersions(items);

        let versionToSelect = null;

        if (nextSelectedVersionId) {
          versionToSelect = items.find((v) => sameId(v.id, nextSelectedVersionId)) || null;
        }

        if (!versionToSelect && selectedVersionId) {
          versionToSelect = items.find((v) => sameId(v.id, selectedVersionId)) || null;
        }

        if (!versionToSelect) {
          versionToSelect =
            items.find((v) => v.status === "draft") ||
            items.find((v) => v.is_current) ||
            items[0] ||
            null;
        }

        if (versionToSelect) {
          const full = await getWorkflowTemplateVersion(workflowTemplateId, versionToSelect.id);
          ++actionRequestIdRef.current;
          setSelectedStepId(null);
          setSelectedActionId(null);
          setSteps([]);
          setActions([]);
          setSelectedVersionId(full.id);
          await loadSteps(workflowTemplateId, full.id);
        } else {
          setSelectedVersionId(null);
          setSteps([]);
          setSelectedStepId(null);
          setActions([]);
          setSelectedActionId(null);
        }
      } catch (err) {
        console.error("Failed to load workflow template versions:", err?.response?.data || err?.message || err);
        alert("Failed to load workflow template versions.");
        setVersions([]);
        setSelectedVersionId(null);
        setSteps([]);
        setSelectedStepId(null);
        setActions([]);
        setSelectedActionId(null);
      } finally {
        setLoading((p) => ({ ...p, versions: false }));
      }
    },
    [selectedVersionId, loadSteps]
  );

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      setGridWidth(node.offsetWidth || 1200);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // Defaults are first-use defaults only. Once a scoped widget list exists,
  // it is authoritative, including an intentionally empty list.
  useEffect(() => {
    if (!user) return;

    let ids = [...DEFAULT_WIDGET_IDS];
    let hasSavedWidgets = false;

    try {
      const rawWidgets = localStorage.getItem(storageKeys.widgets);
      if (rawWidgets !== null) {
        const parsedWidgets = safeJsonParse(rawWidgets);
        if (Array.isArray(parsedWidgets)) {
          ids = parsedWidgets
            .map(String)
            .filter((id) => DEFAULT_WIDGET_SET.has(id));
          hasSavedWidgets = true;
        }
      }
    } catch {
      ids = [...DEFAULT_WIDGET_IDS];
    }

    let nextLayout;
    try {
      const rawLayout = localStorage.getItem(storageKeys.layout);
      const parsedLayout = safeJsonParse(rawLayout);
      nextLayout = validateLayout(parsedLayout, ids);
    } catch {
      nextLayout = validateLayout([], ids);
    }

    setActiveWidgetIds(ids);
    setLayout(nextLayout);

    if (!hasSavedWidgets) {
      try {
        localStorage.setItem(storageKeys.widgets, JSON.stringify(ids));
        localStorage.setItem(storageKeys.layout, JSON.stringify(nextLayout));
      } catch {
        // Ignore localStorage write failures.
      }
    }
  }, [user, storageKeys]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadTaxonomyTypes();
    loadWorkPackageTaxonomyTypes();
    loadWorkflowTemplateCategories();
  }, [loadTaxonomyTypes, loadWorkPackageTaxonomyTypes, loadWorkflowTemplateCategories]);

  useEffect(() => {
    if (!templates.length) return;

    const stillExists = selectedTemplateId
      ? templates.some((t) => sameId(t.id, selectedTemplateId))
      : false;

    if (!stillExists) {
      const firstTemplate = templates[0];
      setSelectedTemplateId(firstTemplate.id);
    }
  }, [templates, selectedTemplateId]);

  useEffect(() => {
    if (selectedTemplateId) {
      loadVersions(selectedTemplateId, null);
      loadBindings(selectedTemplateId);
    } else {
      setVersions([]);
      setSelectedVersionId(null);
      setSteps([]);
      setSelectedStepId(null);
      setActions([]);
      setSelectedActionId(null);
      setBindings([]);
      setSelectedBindingId(null);
    }
  }, [selectedTemplateId, loadVersions, loadBindings]);

  useEffect(() => {
    if (selectedTemplateId && selectedVersionId && selectedStepId &&
        steps.some((step) => sameId(step.id, selectedStepId))) {
      loadActions(selectedTemplateId, selectedVersionId, selectedStepId);
    } else {
      setActions([]);
      setSelectedActionId(null);
    }
  }, [selectedTemplateId, selectedVersionId, selectedStepId, steps, loadActions]);

  const handleFiltersChange = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
  };

  const handleSelectTemplate = (template) => {
    const templateId = template?.id ?? null;
    setSelectedTemplateId(templateId);
    setSelectedVersionId(null);
    setSelectedStepId(null);
    setSelectedActionId(null);
    setSelectedBindingId(null);
    setVersions([]);
    setSteps([]);
    setActions([]);
    setBindings([]);
  };

  const handleSelectVersion = async (version) => {
    if (!selectedTemplateId || !version?.id) return;

    try {
      const full = await getWorkflowTemplateVersion(selectedTemplateId, version.id);
      ++actionRequestIdRef.current;
      setSelectedStepId(null);
      setSelectedActionId(null);
      setSteps([]);
      setActions([]);
      setSelectedVersionId(full.id);
      await loadSteps(selectedTemplateId, full.id);
    } catch (err) {
      console.error("Failed to load workflow template version:", err?.response?.data || err?.message || err);
      alert("Failed to load workflow template version.");
    }
  };

  const handleCreateTemplate = async (payload, onSuccess) => {
    setLoading((p) => ({ ...p, creatingTemplate: true }));
    try {
      const created = await createWorkflowTemplate(payload);
      await loadTemplates();

      setSelectedTemplateId(created.id);
      setSelectedVersionId(null);
      setSelectedStepId(null);
      setSelectedActionId(null);
      setSelectedBindingId(null);
      setVersions([]);
      setSteps([]);
      setActions([]);
      setBindings([]);

      if (typeof onSuccess === "function") onSuccess();
    } catch (err) {
      console.error("Failed to create workflow template:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to create workflow template.";
      alert(typeof detail === "string" ? detail : "Failed to create workflow template.");
    } finally {
      setLoading((p) => ({ ...p, creatingTemplate: false }));
    }
  };

  const handleArchiveTemplate = async (workflowTemplateId) => {
    if (!workflowTemplateId) return;

    const template = templates.find((row) => sameId(row.id, workflowTemplateId));
    const label = template?.name || `workflow template ${workflowTemplateId}`;
    if (!window.confirm(`Archive ${label}? Existing runtime history is preserved.`)) return;

    setLoading((p) => ({ ...p, archivingTemplate: true }));
    try {
      await archiveWorkflowTemplate(workflowTemplateId);
      if (sameId(selectedTemplateId, workflowTemplateId)) {
        setSelectedTemplateId(null);
        setSelectedVersionId(null);
        setSelectedStepId(null);
        setSelectedActionId(null);
      }
      await loadTemplates();
    } catch (err) {
      console.error("Failed to archive workflow template:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to archive workflow template.";
      alert(typeof detail === "string" ? detail : "Failed to archive workflow template.");
    } finally {
      setLoading((p) => ({ ...p, archivingTemplate: false }));
    }
  };

  const handleCreateDraft = async (payload) => {
    if (!selectedTemplateId) {
      alert("Select a template first.");
      return;
    }

    setLoading((p) => ({ ...p, creatingDraft: true }));
    try {
      const created = await createWorkflowTemplateDraft(selectedTemplateId, payload);
      await loadVersions(selectedTemplateId, created.id);
    } catch (err) {
      console.error("Failed to create draft workflow version:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to create draft workflow version.";
      alert(typeof detail === "string" ? detail : "Failed to create draft workflow version.");
    } finally {
      setLoading((p) => ({ ...p, creatingDraft: false }));
    }
  };

  const handleSaveTemplate = async (payload) => {
    if (!selectedTemplateId) {
      alert("No template selected.");
      return;
    }

    setLoading((p) => ({ ...p, savingTemplate: true }));
    try {
      await updateWorkflowTemplate(selectedTemplateId, payload);
      await loadTemplates();
      await loadWorkflowTemplateCategories();
    } catch (err) {
      console.error("Failed to update workflow template:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to update workflow template.";
      alert(typeof detail === "string" ? detail : "Failed to update workflow template.");
    } finally {
      setLoading((p) => ({ ...p, savingTemplate: false }));
    }
  };

  const handlePublishVersion = async (payload) => {
    if (!selectedTemplateId || !selectedVersionId) {
      alert("Select a draft version first.");
      return;
    }

    setLoading((p) => ({ ...p, publishing: true }));
    try {
      await publishWorkflowTemplateVersion(selectedTemplateId, selectedVersionId, payload);
      await loadTemplates();
      await loadVersions(selectedTemplateId, selectedVersionId);
      await loadBindings(selectedTemplateId);
    } catch (err) {
      console.error("Failed to publish workflow version:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to publish workflow version.";
      alert(typeof detail === "string" ? detail : "Failed to publish workflow version.");
    } finally {
      setLoading((p) => ({ ...p, publishing: false }));
    }
  };

  const handleCreateStep = async (payload) => {
    if (!selectedTemplateId || !selectedVersionId) {
      alert("Select a draft version first.");
      return;
    }

    setLoading((p) => ({ ...p, savingStep: true }));
    try {
      const created = await createWorkflowTemplateStep(selectedTemplateId, selectedVersionId, payload);
      await loadSteps(selectedTemplateId, selectedVersionId);
      setSelectedStepId(created?.id || null);
    } catch (err) {
      console.error("Failed to create workflow template step:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to create workflow template step.";
      alert(typeof detail === "string" ? detail : "Failed to create workflow template step.");
    } finally {
      setLoading((p) => ({ ...p, savingStep: false }));
    }
  };

  const handleUpdateStep = async (workflowTemplateStepId, payload) => {
    if (!selectedTemplateId || !selectedVersionId || !workflowTemplateStepId) {
      alert("Select a step first.");
      return;
    }

    setLoading((p) => ({ ...p, savingStep: true }));
    try {
      const updated = await updateWorkflowTemplateStep(
        selectedTemplateId,
        selectedVersionId,
        workflowTemplateStepId,
        payload
      );
      await loadSteps(selectedTemplateId, selectedVersionId);
      setSelectedStepId(updated?.id || workflowTemplateStepId);
    } catch (err) {
      console.error("Failed to update workflow template step:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to update workflow template step.";
      alert(typeof detail === "string" ? detail : "Failed to update workflow template step.");
    } finally {
      setLoading((p) => ({ ...p, savingStep: false }));
    }
  };

  const handleDeleteStep = async (workflowTemplateStepId) => {
    if (!selectedTemplateId || !selectedVersionId || !workflowTemplateStepId) {
      alert("Select a step first.");
      return;
    }

    setLoading((p) => ({ ...p, deletingStep: true }));
    try {
      await deleteWorkflowTemplateStep(selectedTemplateId, selectedVersionId, workflowTemplateStepId);
      await loadSteps(selectedTemplateId, selectedVersionId);
    } catch (err) {
      console.error("Failed to delete workflow template step:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to delete workflow template step.";
      alert(typeof detail === "string" ? detail : "Failed to delete workflow template step.");
    } finally {
      setLoading((p) => ({ ...p, deletingStep: false }));
    }
  };

  const handleMoveStepUp = async (step, index) => {
    if (!selectedTemplateId || !selectedVersionId || index <= 0) return;

    const prev = steps[index - 1];
    if (!prev) return;

    setLoading((p) => ({ ...p, reorderingStep: true }));
    try {
      await updateWorkflowTemplateStep(selectedTemplateId, selectedVersionId, step.id, {
        sequence_no: prev.sequence_no,
      });
      await updateWorkflowTemplateStep(selectedTemplateId, selectedVersionId, prev.id, {
        sequence_no: step.sequence_no,
      });
      await loadSteps(selectedTemplateId, selectedVersionId);
      setSelectedStepId(step.id);
    } catch (err) {
      console.error("Failed to reorder step up:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to reorder step.";
      alert(typeof detail === "string" ? detail : "Failed to reorder step.");
    } finally {
      setLoading((p) => ({ ...p, reorderingStep: false }));
    }
  };

  const handleMoveStepDown = async (step, index) => {
    if (!selectedTemplateId || !selectedVersionId || index >= steps.length - 1) return;

    const next = steps[index + 1];
    if (!next) return;

    setLoading((p) => ({ ...p, reorderingStep: true }));
    try {
      await updateWorkflowTemplateStep(selectedTemplateId, selectedVersionId, step.id, {
        sequence_no: next.sequence_no,
      });
      await updateWorkflowTemplateStep(selectedTemplateId, selectedVersionId, next.id, {
        sequence_no: step.sequence_no,
      });
      await loadSteps(selectedTemplateId, selectedVersionId);
      setSelectedStepId(step.id);
    } catch (err) {
      console.error("Failed to reorder step down:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to reorder step.";
      alert(typeof detail === "string" ? detail : "Failed to reorder step.");
    } finally {
      setLoading((p) => ({ ...p, reorderingStep: false }));
    }
  };

  const handleCreateChecklist = async (payload) => {
    if (!selectedTemplateId || !selectedVersionId) return;
    setLoading((p) => ({ ...p, savingChecklist: true }));
    try { await createWorkflowTemplateChecklist(selectedTemplateId, selectedVersionId, payload); await loadChecklists(selectedTemplateId, selectedVersionId); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to create checklist."); }
    finally { setLoading((p) => ({ ...p, savingChecklist: false })); }
  };
  const handleRenameChecklist = async (id, name) => {
    try { await updateWorkflowTemplateChecklist(selectedTemplateId, selectedVersionId, id, { name }); await loadChecklists(selectedTemplateId, selectedVersionId); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to update checklist."); }
  };
  const handleSetChecklistSteps = async (id, stepIds) => {
    try { await setWorkflowTemplateChecklistSteps(selectedTemplateId, selectedVersionId, id, stepIds); await loadChecklists(selectedTemplateId, selectedVersionId); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to update checklist steps."); }
  };
  const handleDeleteChecklist = async (id) => {
    try { await deleteWorkflowTemplateChecklist(selectedTemplateId, selectedVersionId, id); await loadChecklists(selectedTemplateId, selectedVersionId); }
    catch (err) { alert(err?.response?.data?.detail || "Failed to delete checklist."); }
  };

  const handleCreateAction = async (payload) => {
    if (!selectedTemplateId || !selectedVersionId || !selectedStepId) {
      alert("Select a draft version and step first.");
      return;
    }

    setLoading((p) => ({ ...p, savingAction: true }));
    try {
      const created = await createWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        payload
      );
      await loadActions(selectedTemplateId, selectedVersionId, selectedStepId);
      setSelectedActionId(created?.id || null);
    } catch (err) {
      console.error("Failed to create workflow template step action:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to create workflow template step action.";
      alert(typeof detail === "string" ? detail : "Failed to create workflow template step action.");
    } finally {
      setLoading((p) => ({ ...p, savingAction: false }));
    }
  };

  const handleUpdateAction = async (workflowTemplateStepActionId, payload) => {
    if (!selectedTemplateId || !selectedVersionId || !selectedStepId || !workflowTemplateStepActionId) {
      alert("Select an action first.");
      return;
    }

    setLoading((p) => ({ ...p, savingAction: true }));
    try {
      const updated = await updateWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        workflowTemplateStepActionId,
        payload
      );
      await loadActions(selectedTemplateId, selectedVersionId, selectedStepId);
      setSelectedActionId(updated?.id || workflowTemplateStepActionId);
    } catch (err) {
      console.error("Failed to update workflow template step action:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to update workflow template step action.";
      alert(typeof detail === "string" ? detail : "Failed to update workflow template step action.");
    } finally {
      setLoading((p) => ({ ...p, savingAction: false }));
    }
  };

  const handleDeleteAction = async (workflowTemplateStepActionId) => {
    if (!selectedTemplateId || !selectedVersionId || !selectedStepId || !workflowTemplateStepActionId) {
      alert("Select an action first.");
      return;
    }

    setLoading((p) => ({ ...p, deletingAction: true }));
    try {
      await deleteWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        workflowTemplateStepActionId
      );
      await loadActions(selectedTemplateId, selectedVersionId, selectedStepId);
    } catch (err) {
      console.error("Failed to delete workflow template step action:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to delete workflow template step action.";
      alert(typeof detail === "string" ? detail : "Failed to delete workflow template step action.");
    } finally {
      setLoading((p) => ({ ...p, deletingAction: false }));
    }
  };

  const handleMoveActionUp = async (action, index) => {
    if (!selectedTemplateId || !selectedVersionId || !selectedStepId || index <= 0) return;

    const prev = actions[index - 1];
    if (!prev) return;

    setLoading((p) => ({ ...p, reorderingAction: true }));
    try {
      await updateWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        action.id,
        { sequence_no: prev.sequence_no }
      );
      await updateWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        prev.id,
        { sequence_no: action.sequence_no }
      );
      await loadActions(selectedTemplateId, selectedVersionId, selectedStepId);
      setSelectedActionId(action.id);
    } catch (err) {
      console.error("Failed to reorder action up:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to reorder action.";
      alert(typeof detail === "string" ? detail : "Failed to reorder action.");
    } finally {
      setLoading((p) => ({ ...p, reorderingAction: false }));
    }
  };

  const handleMoveActionDown = async (action, index) => {
    if (!selectedTemplateId || !selectedVersionId || !selectedStepId || index >= actions.length - 1) return;

    const next = actions[index + 1];
    if (!next) return;

    setLoading((p) => ({ ...p, reorderingAction: true }));
    try {
      await updateWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        action.id,
        { sequence_no: next.sequence_no }
      );
      await updateWorkflowTemplateStepAction(
        selectedTemplateId,
        selectedVersionId,
        selectedStepId,
        next.id,
        { sequence_no: action.sequence_no }
      );
      await loadActions(selectedTemplateId, selectedVersionId, selectedStepId);
      setSelectedActionId(action.id);
    } catch (err) {
      console.error("Failed to reorder action down:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to reorder action.";
      alert(typeof detail === "string" ? detail : "Failed to reorder action.");
    } finally {
      setLoading((p) => ({ ...p, reorderingAction: false }));
    }
  };

  const handleCreateBinding = async (payload) => {
    setLoading((p) => ({ ...p, savingBinding: true }));
    try {
      const created = await createWorkflowTemplateBinding(payload);
      await loadBindings(payload.workflow_template_id);
      setSelectedBindingId(created?.id || null);
    } catch (err) {
      console.error("Failed to create workflow template binding:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to create workflow template binding.";
      alert(typeof detail === "string" ? detail : "Failed to create workflow template binding.");
    } finally {
      setLoading((p) => ({ ...p, savingBinding: false }));
    }
  };

  const handleUpdateBinding = async (workflowTemplateBindingId, payload) => {
    if (!selectedTemplateId || !workflowTemplateBindingId) {
      alert("Select a binding first.");
      return;
    }

    setLoading((p) => ({ ...p, savingBinding: true }));
    try {
      const updated = await updateWorkflowTemplateBinding(workflowTemplateBindingId, payload);
      await loadBindings(selectedTemplateId);
      setSelectedBindingId(updated?.id || workflowTemplateBindingId);
    } catch (err) {
      console.error("Failed to update workflow template binding:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to update workflow template binding.";
      alert(typeof detail === "string" ? detail : "Failed to update workflow template binding.");
    } finally {
      setLoading((p) => ({ ...p, savingBinding: false }));
    }
  };

  const handleDeactivateBinding = async (workflowTemplateBindingId, payload) => {
    if (!selectedTemplateId || !workflowTemplateBindingId) {
      alert("Select a binding first.");
      return;
    }

    setLoading((p) => ({ ...p, deactivatingBinding: true }));
    try {
      await deactivateWorkflowTemplateBinding(workflowTemplateBindingId, payload);
      await loadBindings(selectedTemplateId);
    } catch (err) {
      console.error("Failed to deactivate workflow template binding:", err?.response?.data || err?.message || err);
      const detail = err?.response?.data?.detail || "Failed to deactivate workflow template binding.";
      alert(typeof detail === "string" ? detail : "Failed to deactivate workflow template binding.");
    } finally {
      setLoading((p) => ({ ...p, deactivatingBinding: false }));
    }
  };

  const persistWidgets = useCallback(
    (ids) => {
      try {
        localStorage.setItem(storageKeys.widgets, JSON.stringify(ids));
      } catch {
        // Ignore localStorage write failures.
      }
    },
    [storageKeys.widgets]
  );

  const persistLayout = useCallback(
    (nextLayout) => {
      try {
        localStorage.setItem(storageKeys.layout, JSON.stringify(nextLayout));
      } catch {
        // Ignore localStorage write failures.
      }
    },
    [storageKeys.layout]
  );

  const onLayoutChange = useCallback(
    (newLayout) => {
      const next = validateLayout(newLayout, activeWidgetIds);
      setLayout(next);
      persistLayout(next);
    },
    [activeWidgetIds, persistLayout]
  );

  const addWidgetById = useCallback(
    (raw) => {
      if (!canManageWidgets) return;

      const id = String(raw?.id ?? raw?.widget_id ?? raw?.name ?? raw);
      if (!DEFAULT_WIDGET_SET.has(id)) return;
      if (activeWidgetIds.includes(id)) {
        setShowAddModal(false);
        return;
      }

      const nextIds = [...activeWidgetIds, id];
      const nextLayout = validateLayout(layout, nextIds);

      setActiveWidgetIds(nextIds);
      setLayout(nextLayout);
      persistWidgets(nextIds);
      persistLayout(nextLayout);
      setShowAddModal(false);
    },
    [
      activeWidgetIds,
      canManageWidgets,
      layout,
      persistLayout,
      persistWidgets,
    ]
  );

  const removeWidgetById = useCallback(
    (widgetId) => {
      if (!canManageWidgets) return;

      const id = String(widgetId);
      const nextIds = activeWidgetIds.filter((candidate) => candidate !== id);
      const nextLayout = layout.filter((item) => item.i !== id);

      setActiveWidgetIds(nextIds);
      setLayout(nextLayout);
      persistWidgets(nextIds);
      persistLayout(nextLayout);
    },
    [
      activeWidgetIds,
      canManageWidgets,
      layout,
      persistLayout,
      persistWidgets,
    ]
  );

  const mobileLayoutItems = useMemo(() => {
    const active = new Set(activeWidgetIds.map(String));

    const positioned = layout
      .filter((item) => active.has(String(item.i)))
      .slice()
      .sort((a, b) => {
        const ay = Number.isFinite(Number(a?.y)) ? Number(a.y) : Number.MAX_SAFE_INTEGER;
        const by = Number.isFinite(Number(b?.y)) ? Number(b.y) : Number.MAX_SAFE_INTEGER;
        if (ay !== by) return ay - by;

        const ax = Number.isFinite(Number(a?.x)) ? Number(a.x) : Number.MAX_SAFE_INTEGER;
        const bx = Number.isFinite(Number(b?.x)) ? Number(b.x) : Number.MAX_SAFE_INTEGER;
        if (ax !== bx) return ax - bx;

        return String(a?.i || "").localeCompare(String(b?.i || ""));
      });

    const positionedIds = new Set(positioned.map((item) => String(item.i)));
    const missing = activeWidgetIds
      .filter((id) => !positionedIds.has(String(id)))
      .map((id) => {
        const base = defaultLayout.find((item) => item.i === id);
        return base || { i: id, x: 0, y: Number.MAX_SAFE_INTEGER, w: 12, h: 6 };
      });

    return [...positioned, ...missing];
  }, [activeWidgetIds, layout]);

  const getMobilePanelHeight = useCallback(
    (item) => {
      const h = Number.isFinite(Number(item?.h)) ? Math.max(1, Number(item.h)) : 6;
      return Math.max(320, h * rowHeight + Math.max(0, h - 1) * 24);
    },
    [rowHeight]
  );

  const renderWorkflowPanel = useCallback(
    (panelId) => {
      switch (panelId) {
        case "template_list":
          return (
            <WidgetWrapper
              title="Templates"
              onRemove={canManageWidgets ? () => removeWidgetById("template_list") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowTemplateListWidget
                  templates={templates}
                  versions={versions}
                  workflowTemplateCategories={workflowTemplateCategories}
                  selectedTemplateId={selectedTemplateId}
                  selectedVersionId={selectedVersionId}
                  filters={filters}
                  loading={{
                    templates: loading.templates,
                    versions: loading.versions,
                    categories: loading.categories,
                    creatingTemplate: loading.creatingTemplate,
                    creatingDraft: loading.creatingDraft,
                    archivingTemplate: loading.archivingTemplate,
                  }}
                  onFiltersChange={handleFiltersChange}
                  onSelectTemplate={handleSelectTemplate}
                  onSelectVersion={handleSelectVersion}
                  onCreateTemplate={handleCreateTemplate}
                  onCreateDraft={handleCreateDraft}
                  onArchiveTemplate={handleArchiveTemplate}
                  onCategoriesChanged={loadWorkflowTemplateCategories}
                />
              </div>
            </WidgetWrapper>
          );

        case "draft_header":
          return (
            <WidgetWrapper
              title="Draft / Version Context"
              onRemove={canManageWidgets ? () => removeWidgetById("draft_header") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowDraftHeaderWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  workflowTemplateCategories={workflowTemplateCategories}
                  loading={{
                    savingTemplate: loading.savingTemplate,
                    creatingDraft: loading.creatingDraft,
                    archivingTemplate: loading.archivingTemplate,
                    publishing: loading.publishing,
                  }}
                  onSaveTemplate={handleSaveTemplate}
                  onCreateDraft={handleCreateDraft}
                  onArchiveTemplate={handleArchiveTemplate}
                  onPublishVersion={handlePublishVersion}
                />
              </div>
            </WidgetWrapper>
          );

        case "validation":
          return (
            <WidgetWrapper
              title="Validation Summary"
              onRemove={canManageWidgets ? () => removeWidgetById("validation") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowValidationSummaryWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  steps={steps}
                  actions={actions}
                  bindings={bindings}
                />
              </div>
            </WidgetWrapper>
          );

        case "steps":
          return (
            <WidgetWrapper
              title="Step Builder"
              onRemove={canManageWidgets ? () => removeWidgetById("steps") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowStepBuilderWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  steps={steps}
                  selectedStepId={selectedStepId}
                  loading={{
                    steps: loading.steps,
                    savingStep: loading.savingStep,
                    deletingStep: loading.deletingStep,
                    reorderingStep: loading.reorderingStep,
                  }}
                  onSelectStep={(id) => {
                    setSelectedStepId(id);
                    setSelectedActionId(null);
                  }}
                  onCreateStep={handleCreateStep}
                  onUpdateStep={handleUpdateStep}
                  onDeleteStep={handleDeleteStep}
                  onMoveStepUp={handleMoveStepUp}
                  onMoveStepDown={handleMoveStepDown}
                />
              </div>
            </WidgetWrapper>
          );

        case "checklists":
          return (
            <WidgetWrapper title="Checklists" onRemove={canManageWidgets ? () => removeWidgetById("checklists") : undefined} showRemoveButton={canManageWidgets}>
              <div className="h-full min-h-0"><WorkflowChecklistBuilderWidget selectedVersion={selectedVersion} steps={steps} checklists={checklists} loading={{checklists:loading.checklists,saving:loading.savingChecklist}} onCreate={handleCreateChecklist} onRename={handleRenameChecklist} onDelete={handleDeleteChecklist} onSetSteps={handleSetChecklistSteps}/></div>
            </WidgetWrapper>
          );

        case "actions":
          return (
            <WidgetWrapper
              title="Advanced Automation"
              onRemove={canManageWidgets ? () => removeWidgetById("actions") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowActionConfigWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  selectedStep={selectedStep}
                  actions={actions}
                  selectedActionId={selectedActionId}
                  loading={{
                    actions: loading.actions,
                    savingAction: loading.savingAction,
                    deletingAction: loading.deletingAction,
                    reorderingAction: loading.reorderingAction,
                  }}
                  onSelectAction={setSelectedActionId}
                  onCreateAction={handleCreateAction}
                  onUpdateAction={handleUpdateAction}
                  onDeleteAction={handleDeleteAction}
                  onMoveActionUp={handleMoveActionUp}
                  onMoveActionDown={handleMoveActionDown}
                />
              </div>
            </WidgetWrapper>
          );

        case "bindings":
          return (
            <WidgetWrapper
              title="Bindings"
              onRemove={canManageWidgets ? () => removeWidgetById("bindings") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowBindingAdminWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  bindings={bindings}
                  selectedBindingId={selectedBindingId}
                  taxonomyTypes={taxonomyTypes}
                  workPackageTaxonomyTypes={workPackageTaxonomyTypes}
                  loading={{
                    bindings: loading.bindings,
                    taxonomy: loading.taxonomy,
                    workPackageTaxonomy: loading.workPackageTaxonomy,
                    savingBinding: loading.savingBinding,
                    deactivatingBinding: loading.deactivatingBinding,
                  }}
                  onSelectBinding={setSelectedBindingId}
                  onCreateBinding={handleCreateBinding}
                  onUpdateBinding={handleUpdateBinding}
                  onDeactivateBinding={handleDeactivateBinding}
                />
              </div>
            </WidgetWrapper>
          );

        case "publish":
          return (
            <WidgetWrapper
              title="Publish Controls"
              onRemove={canManageWidgets ? () => removeWidgetById("publish") : undefined}
              showRemoveButton={canManageWidgets}
            >
              <div className="h-full min-h-0">
                <WorkflowPublishControlsWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  validation={validationSummary}
                  loading={{
                    publishing: loading.publishing,
                  }}
                  onPublishVersion={handlePublishVersion}
                />
              </div>
            </WidgetWrapper>
          );

        default:
          return null;
      }
    },
    [
      actions,
      checklists,
      bindings,
      canManageWidgets,
      filters,
      handleCreateAction,
      handleCreateChecklist,
      handleRenameChecklist,
      handleDeleteChecklist,
      handleSetChecklistSteps,
      handleCreateBinding,
      handleCreateDraft,
      handleCreateStep,
      handleCreateTemplate,
      handleDeactivateBinding,
      handleDeleteAction,
      handleDeleteStep,
      handleFiltersChange,
      handleMoveActionDown,
      handleMoveActionUp,
      handleMoveStepDown,
      handleMoveStepUp,
      handlePublishVersion,
      handleSaveTemplate,
      handleSelectTemplate,
      handleSelectVersion,
      handleUpdateAction,
      handleUpdateBinding,
      handleUpdateStep,
      loadWorkflowTemplateCategories,
      loading,
      removeWidgetById,
      selectedActionId,
      selectedBindingId,
      selectedStep,
      selectedStepId,
      selectedTemplate,
      selectedTemplateId,
      selectedVersion,
      selectedVersionId,
      steps,
      taxonomyTypes,
      workPackageTaxonomyTypes,
      validationSummary,
      versions,
      workflowTemplateCategories,
    ]
  );

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingLeft: isMobile ? 12 : containerPadding,
        paddingRight: isMobile ? 12 : containerPadding,
        paddingTop: isMobile ? 6 : undefined,
        paddingBottom: isMobile ? 16 : undefined,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            height: isMobile ? "auto" : headerHeight,
            minHeight: isMobile ? 58 : undefined,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
            marginBottom: isMobile ? 6 : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              margin: 0,
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
            }}
          >
            Workflow Builder
          </h2>

          {canManageWidgets && (
            <button
              type="button"
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick text-white px-3 py-2 rounded"
              onClick={() => setShowAddModal(true)}
              style={{
                minHeight: isMobile ? 44 : undefined,
                whiteSpace: "nowrap",
                flex: "0 0 auto",
              }}
            >
              Add Widget
            </button>
          )}
        </div>

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
              minWidth: 0,
              paddingBottom: 8,
            }}
          >
            {mobileLayoutItems.map((item) => (
              <div
                key={item.i}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobilePanelHeight(item)}px`,
                  overflow: "hidden",
                }}
              >
                {renderWorkflowPanel(item.i)}
              </div>
            ))}
          </div>
        ) : (
<GridLayout
          className="layout"
          layout={layout.filter((item) => activeWidgetIds.includes(item.i))}
          cols={12}
          rowHeight={rowHeight}
          width={gridWidth}
          resizeHandles={["se"]}
          draggableHandle=".widget-drag-handle"
          isResizable={true}
          isDraggable={true}
          onLayoutChange={onLayoutChange}
          compactType={null}
          preventCollision={false}
          margin={[16, 24]}
        >
          {activeWidgetIds.includes("template_list") && (
            <div key="template_list" className="h-full min-h-0">
              <WidgetWrapper
                title="Templates"
                onRemove={canManageWidgets ? () => removeWidgetById("template_list") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowTemplateListWidget
                  templates={templates}
                  versions={versions}
                  workflowTemplateCategories={workflowTemplateCategories}
                  selectedTemplateId={selectedTemplateId}
                  selectedVersionId={selectedVersionId}
                  filters={filters}
                  loading={{
                    templates: loading.templates,
                    versions: loading.versions,
                    categories: loading.categories,
                    creatingTemplate: loading.creatingTemplate,
                    creatingDraft: loading.creatingDraft,
                    archivingTemplate: loading.archivingTemplate,
                  }}
                  onFiltersChange={handleFiltersChange}
                  onSelectTemplate={handleSelectTemplate}
                  onSelectVersion={handleSelectVersion}
                  onCreateTemplate={handleCreateTemplate}
                  onCreateDraft={handleCreateDraft}
                  onArchiveTemplate={handleArchiveTemplate}
                  onCategoriesChanged={loadWorkflowTemplateCategories}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("draft_header") && (
            <div key="draft_header" className="h-full min-h-0">
              <WidgetWrapper
                title="Draft / Version Context"
                onRemove={canManageWidgets ? () => removeWidgetById("draft_header") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowDraftHeaderWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  workflowTemplateCategories={workflowTemplateCategories}
                  loading={{
                    savingTemplate: loading.savingTemplate,
                    creatingDraft: loading.creatingDraft,
                    archivingTemplate: loading.archivingTemplate,
                    publishing: loading.publishing,
                  }}
                  onSaveTemplate={handleSaveTemplate}
                  onCreateDraft={handleCreateDraft}
                  onArchiveTemplate={handleArchiveTemplate}
                  onPublishVersion={handlePublishVersion}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("validation") && (
            <div key="validation" className="h-full min-h-0">
              <WidgetWrapper
                title="Validation Summary"
                onRemove={canManageWidgets ? () => removeWidgetById("validation") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowValidationSummaryWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  steps={steps}
                  actions={actions}
                  bindings={bindings}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("steps") && (
            <div key="steps" className="h-full min-h-0">
              <WidgetWrapper
                title="Step Builder"
                onRemove={canManageWidgets ? () => removeWidgetById("steps") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowStepBuilderWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  steps={steps}
                  selectedStepId={selectedStepId}
                  loading={{
                    steps: loading.steps,
                    savingStep: loading.savingStep,
                    deletingStep: loading.deletingStep,
                    reorderingStep: loading.reorderingStep,
                  }}
                  onSelectStep={(id) => {
                    setSelectedStepId(id);
                    setSelectedActionId(null);
                  }}
                  onCreateStep={handleCreateStep}
                  onUpdateStep={handleUpdateStep}
                  onDeleteStep={handleDeleteStep}
                  onMoveStepUp={handleMoveStepUp}
                  onMoveStepDown={handleMoveStepDown}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("checklists") && (
            <div key="checklists" className="h-full min-h-0">
              <WidgetWrapper title="Checklists" onRemove={canManageWidgets ? () => removeWidgetById("checklists") : undefined} showRemoveButton={canManageWidgets}>
                <div className="h-full min-h-0"><WorkflowChecklistBuilderWidget selectedVersion={selectedVersion} steps={steps} checklists={checklists} loading={{checklists:loading.checklists,saving:loading.savingChecklist}} onCreate={handleCreateChecklist} onRename={handleRenameChecklist} onDelete={handleDeleteChecklist} onSetSteps={handleSetChecklistSteps}/></div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("actions") && (
            <div key="actions" className="h-full min-h-0">
              <WidgetWrapper
                title="Advanced Automation"
                onRemove={canManageWidgets ? () => removeWidgetById("actions") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowActionConfigWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  selectedStep={selectedStep}
                  actions={actions}
                  selectedActionId={selectedActionId}
                  loading={{
                    actions: loading.actions,
                    savingAction: loading.savingAction,
                    deletingAction: loading.deletingAction,
                    reorderingAction: loading.reorderingAction,
                  }}
                  onSelectAction={setSelectedActionId}
                  onCreateAction={handleCreateAction}
                  onUpdateAction={handleUpdateAction}
                  onDeleteAction={handleDeleteAction}
                  onMoveActionUp={handleMoveActionUp}
                  onMoveActionDown={handleMoveActionDown}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("bindings") && (
            <div key="bindings" className="h-full min-h-0">
              <WidgetWrapper
                title="Bindings"
                onRemove={canManageWidgets ? () => removeWidgetById("bindings") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowBindingAdminWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  bindings={bindings}
                  selectedBindingId={selectedBindingId}
                  taxonomyTypes={taxonomyTypes}
                  workPackageTaxonomyTypes={workPackageTaxonomyTypes}
                  loading={{
                    bindings: loading.bindings,
                    taxonomy: loading.taxonomy,
                    workPackageTaxonomy: loading.workPackageTaxonomy,
                    savingBinding: loading.savingBinding,
                    deactivatingBinding: loading.deactivatingBinding,
                  }}
                  onSelectBinding={setSelectedBindingId}
                  onCreateBinding={handleCreateBinding}
                  onUpdateBinding={handleUpdateBinding}
                  onDeactivateBinding={handleDeactivateBinding}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}

          {activeWidgetIds.includes("publish") && (
            <div key="publish" className="h-full min-h-0">
              <WidgetWrapper
                title="Publish Controls"
                onRemove={canManageWidgets ? () => removeWidgetById("publish") : undefined}
                showRemoveButton={canManageWidgets}
              >
              <div className="h-full min-h-0">
                <WorkflowPublishControlsWidget
                  selectedTemplate={selectedTemplate}
                  selectedVersion={selectedVersion}
                  validation={validationSummary}
                  loading={{
                    publishing: loading.publishing,
                  }}
                  onPublishVersion={handlePublishVersion}
                />
              </div>
              </WidgetWrapper>
            </div>
          )}
        </GridLayout>
        )}

        {showAddModal && canManageWidgets && (
          <AddWidgetModal
            widgets={WORKFLOW_WIDGET_CATALOG}
            onClose={() => setShowAddModal(false)}
            onAdd={addWidgetById}
            onAddWidget={addWidgetById}
            onSelect={addWidgetById}
          />
        )}
      </div>
    </div>
  );
};

export default WorkflowPage;